import { CommandPalette } from "@app/components/command_palette/CommandPalette";
import { useCommandPalette } from "@app/components/command_palette/CommandPaletteContext";
import { DEV_MODE_ACTIVE } from "@app/components/dev/devModeConstants";
import { useDesktopNavigation } from "@app/components/navigation/DesktopNavigationContext";
import { Navigation } from "@app/components/navigation/Navigation";
import { SidebarUserMenu } from "@app/components/navigation/SidebarUserMenu";
import { TopBanners } from "@app/components/navigation/TopBanners";
import { useAppLayout } from "@app/components/sparkle/AppLayoutContext";
import { AppLayoutTitle } from "@app/components/sparkle/AppLayoutTitle";
import { useAppKeyboardShortcuts } from "@app/hooks/useAppKeyboardShortcuts";
import { useDocumentScrollMode } from "@app/hooks/useDocumentScrollMode";
import { useDocumentTitle } from "@app/hooks/useDocumentTitle";
import { useHashParam } from "@app/hooks/useHashParams";
import { useAuth, useWorkspace } from "@app/lib/auth/AuthContext";
import { MOBILE_DOCUMENT_SCROLL_CLASSES } from "@app/lib/documentScrollLayoutClasses";
import { useAppRouter } from "@app/lib/platform";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import { useUser } from "@app/lib/swr/user";
import { TRACKING_AREAS, trackEvent } from "@app/lib/tracking";
import { FULL_SCREEN_HASH_PARAM } from "@app/types/conversation_side_panel";
import { AzimutWorkspaceBar, AzimutWorkspaceFrame, cn } from "@dust-tt/sparkle";
import { useLingui } from "@lingui/react/macro";
import type React from "react";
import { lazy, Suspense } from "react";

// Lazy-load the dev panel only when dev mode is active.
// The module is never fetched/parsed/executed when dev mode is off.
const DevFeatureFlagPanel = DEV_MODE_ACTIVE
  ? lazy(() =>
      // Dynamic import is necessary here: the dev panel must not be bundled
      // in the main chunk — it should only load when dev mode is active.
      import("@app/components/dev/DevFeatureFlagPanel").then((m) => ({
        default: m.DevFeatureFlagPanel,
      }))
    )
  : null;

interface AppContentLayoutProps {
  children: React.ReactNode;
}

interface AppContentInnerWrapperProps {
  isMobile: boolean;
  children: React.ReactNode;
}

function AppContentInnerWrapper({
  isMobile,
  children,
}: AppContentInnerWrapperProps) {
  return (
    <div
      className={cn(
        "az-workspace-canvas",
        !isMobile && "overflow-hidden h-panel"
      )}
    >
      {children}
    </div>
  );
}

export function AppContentLayout({ children }: AppContentLayoutProps) {
  const { t } = useLingui();
  const router = useAppRouter();
  const commandPalette = useCommandPalette();
  const { user: menuUser } = useUser();
  const owner = useWorkspace();
  const isMobile = useIsMobile();
  const { featureFlags, subscription, user } = useAuth();
  const {
    contentClassName,
    contentWidth,
    hasTitle = false,
    hideSidebar = false,
    navChildren,
    pageTitle,
    subNavigation,
    title,
  } = useAppLayout();
  const [fullScreenHash] = useHashParam(FULL_SCREEN_HASH_PARAM);

  const isFullScreen = fullScreenHash === "true";

  const hasTitleBar = !!title || hasTitle;
  useDocumentTitle(pageTitle || `Azimut - ${owner.name}`);
  useAppKeyboardShortcuts(owner);
  const { isNavigationBarOpen, setIsNavigationBarOpen } =
    useDesktopNavigation();

  useDocumentScrollMode(isMobile);

  const isCenteredContent = contentWidth === "centered";
  const hasTitleBarOffsetHeight = hasTitleBar && !!title && isCenteredContent;

  return (
    <AzimutWorkspaceFrame
      skipLabel={t`Skip to content`}
      banners={<TopBanners owner={owner} subscription={subscription} />}
      header={
        <AzimutWorkspaceBar
          homeHref={`/w/${owner.sId}/conversation/new`}
          onHomeClick={(event) => {
            if (
              event.button !== 0 ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey
            ) {
              return;
            }
            event.preventDefault();
            void router.push(`/w/${owner.sId}/conversation/new`);
          }}
          searchLabel={t`Search`}
          onSearch={() => {
            trackEvent({
              area: TRACKING_AREAS.NAVIGATION,
              object: "workspace_search",
            });
            commandPalette.open();
          }}
          userControls={
            menuUser && (
              <SidebarUserMenu
                user={menuUser}
                owner={owner}
                subscription={subscription}
                isFairUseAwuLimitDisabled={featureFlags.includes(
                  "disable_fair_use_awu_limit"
                )}
                placement="header"
              />
            )
          }
        />
      }
      navigation={
        <Navigation
          hideSidebar={hideSidebar}
          isNavigationBarOpen={isNavigationBarOpen}
          setNavigationBarOpen={setIsNavigationBarOpen}
          owner={owner}
          subscription={subscription}
          navChildren={navChildren}
          subNavigation={subNavigation}
          isFullScreen={isFullScreen}
          isMobile={isMobile}
        />
      }
      overlays={
        <>
          <CommandPalette key={owner.sId} owner={owner} user={user} />
          {DevFeatureFlagPanel && (
            <Suspense fallback={null}>
              <DevFeatureFlagPanel serverFlags={featureFlags} />
            </Suspense>
          )}
        </>
      }
    >
      <div
        className={cn(
          "relative flex w-full flex-1 flex-col text-foreground",
          isMobile
            ? MOBILE_DOCUMENT_SCROLL_CLASSES.contentMain
            : "h-full overflow-x-hidden bg-app-background"
        )}
      >
        <AppContentInnerWrapper isMobile={isMobile}>
          <div
            className={cn(
              "flex flex-1 flex-col",
              isMobile
                ? MOBILE_DOCUMENT_SCROLL_CLASSES.contentArea
                : cn(
                    "min-h-0 overflow-y-auto [scrollbar-gutter:stable]",
                    !hasTitleBar && "h-panel"
                  )
            )}
          >
            {/* Temporary measure to preserve title existence on smaller screens.
             * Page has no title, prepend empty AppLayoutTitle. */}
            {!hasTitleBar ? <AppLayoutTitle /> : null}
            {hasTitleBar && contentWidth ? title : null}
            <div
              className={
                contentWidth
                  ? cn(
                      "flex w-full flex-col items-center",
                      isMobile
                        ? MOBILE_DOCUMENT_SCROLL_CLASSES.contentArea
                        : "overflow-y-auto [scrollbar-gutter:stable]",
                      hasTitleBarOffsetHeight &&
                        "h-[calc(var(--panel-height)-3.5rem)]",
                      !hasTitleBarOffsetHeight &&
                        (hasTitleBar || !isMobile) &&
                        "h-full",
                      isCenteredContent ? "pt-4" : "pt-8",
                      contentClassName
                    )
                  : "contents"
              }
            >
              <div
                className={
                  contentWidth
                    ? cn(
                        "flex w-full grow flex-col px-4 md:px-8",
                        isCenteredContent && "max-w-4xl"
                      )
                    : "contents"
                }
              >
                {children}
              </div>
            </div>
          </div>
        </AppContentInnerWrapper>
      </div>
    </AzimutWorkspaceFrame>
  );
}
